type DateLike = string | number | Date | null | undefined;

interface FormatTimestampOptions {
  localTimezone: boolean;
  sourceTimezone?: string | null;
  fallbackTimezone?: string;
  invalidValue?: string;
  // Intl `hour12` override resolved from the user's time-format preference.
  // `undefined` means "let Intl pick the hour cycle from the locale".
  hour12?: boolean;
  // Omit the time-of-day fields (hour/minute/second) — render the date only.
  dateOnly?: boolean;
}

const BASE_DATE_OPTIONS: Omit<Intl.DateTimeFormatOptions, 'timeZone' | 'hour12'> = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
};

const DATE_ONLY_OPTIONS: Omit<Intl.DateTimeFormatOptions, 'timeZone' | 'hour12'> = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
};

export const getLocalTimezoneLabel = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local';

export const formatTimestamp = (
  value: DateLike,
  { localTimezone, sourceTimezone, fallbackTimezone = 'UTC', invalidValue, hour12, dateOnly = false }: FormatTimestampOptions
): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    if (typeof invalidValue === 'string') {
      return invalidValue;
    }
    return typeof value === 'string' ? value : '';
  }

  const timeZone = localTimezone ? undefined : sourceTimezone || fallbackTimezone;

  return new Intl.DateTimeFormat(undefined, {
    ...(dateOnly ? DATE_ONLY_OPTIONS : BASE_DATE_OPTIONS),
    hour12,
    timeZone,
  }).format(date);
};

export const formatDate = (dateProperty: DateLike, localTimezone: boolean): string =>
  formatTimestamp(dateProperty, {
    localTimezone,
    fallbackTimezone: 'UTC',
    invalidValue: '',
  });
